"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  BookOpen,
  Briefcase,
  Building2,
  ChevronDown,
  Compass,
  Facebook,
  FolderOpen,
  Handshake,
  Instagram,
  Layers,
  Linkedin,
  Menu,
  Newspaper,
  Presentation,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/Button";
import { SERVICES } from "@/lib/services";
import { buildContactHref, contextFromPath } from "@/lib/contactContext";
import { contentApi } from "@/lib/contentClient";

type NavMenuId = "services" | "company" | "insights" | null;

type NavItem = { href: string; label: string; hint: string; icon: LucideIcon };

const PRIMARY_LINKS = [
  { href: "/solutions", label: "Solutions" },
  { href: "/portfolio", label: "Work" },
];

const COMPANY_LINKS: NavItem[] = [
  { href: "/about", label: "About", hint: "Our story and point of view", icon: Users },
  { href: "/collab", label: "Collab model", hint: "How the divisions work as one", icon: Handshake },
  { href: "/deck", label: "Company deck", hint: "The pitch, slide by slide", icon: Presentation },
];

const INSIGHTS_LINKS: NavItem[] = [
  { href: "/blog", label: "Blog", hint: "Ideas from the field", icon: Newspaper },
  { href: "/guides", label: "Guides", hint: "How we ship", icon: BookOpen },
  { href: "/resources", label: "Resources", hint: "Tools and downloads", icon: FolderOpen },
];

const OFFER_LINKS: NavItem[] = [
  { href: "/solutions", label: "Solutions", hint: "Outcome-led engagements", icon: Building2 },
  { href: "/portfolio", label: "Work", hint: "Selected projects", icon: Briefcase },
];

const SOCIAL_LINKS = [
  { href: "https://www.linkedin.com/company/doimpo", label: "LinkedIn", icon: Linkedin },
  { href: "https://www.instagram.com/doimpo_collab", label: "Instagram", icon: Instagram },
  { href: "https://www.facebook.com/doimpo", label: "Facebook", icon: Facebook },
];

function pathMatches(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function groupActive(pathname: string, links: { href: string }[]) {
  return links.some((link) => pathMatches(pathname, link.href));
}

const navLinkClass =
  "font-ui relative px-2 py-1.5 text-[13px] font-medium text-zinc-200 transition-colors duration-200 hover:text-white after:absolute after:bottom-0 after:left-2 after:right-2 after:h-px after:scale-x-0 after:bg-accent after:shadow-[0_0_8px_rgba(13,168,159,0.6)] after:transition-transform hover:after:scale-x-100";

const navTriggerActive = "text-white after:scale-x-100";

export default function Header() {
  const pathname = usePathname();
  const talkHref = buildContactHref(contextFromPath(pathname), {
    intent: "callback",
    placement: "navbar-get-started",
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<NavMenuId>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setMobileServicesOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!openMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu]);

  useEffect(() => {
    if (openMenu === "insights") {
      contentApi.prefetchPostsIndex("blog", 1);
    }
  }, [openMenu]);

  useEffect(() => {
    setOpenMenu(null);
    setMobileServicesOpen(false);
  }, [pathname]);

  const frosted = scrolled || menuOpen || Boolean(openMenu);
  const companyActive = groupActive(pathname, COMPANY_LINKS);
  const insightsActive = groupActive(pathname, INSIGHTS_LINKS);
  const servicesActive = pathMatches(pathname, "/services");

  const toggleMenu = (id: Exclude<NavMenuId, null>) => {
    setOpenMenu((current) => (current === id ? null : id));
  };

  return (
    <>
      {mounted && frosted
        ? createPortal(<div className="header-frost-layer" aria-hidden />, document.body)
        : null}
      <AnimatePresence>
        {openMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[90] hidden bg-black/40 backdrop-blur-md lg:block"
            onClick={() => setOpenMenu(null)}
            aria-hidden
          />
        )}
      </AnimatePresence>
      <header className="fixed inset-x-0 top-0 z-[100] border-b border-transparent bg-transparent">
        <div className="relative z-10 mx-auto grid h-14 max-w-page grid-cols-[auto_1fr_auto] items-center gap-3 px-4 sm:px-6 lg:flex lg:justify-between lg:px-8">
          <Link href="/" className="group flex shrink-0 items-center" aria-label="doimpo home">
            <BrandLogo
              variant="mark"
              className="transition-opacity duration-200 group-hover:opacity-90"
              markClassName="h-8 w-auto"
              priority
            />
          </Link>

          <p className="pointer-events-none truncate text-center font-display text-sm font-semibold tracking-tight text-white lg:hidden">
            doimpo <span className="text-gradient-accent">collab</span>
          </p>

          <nav
            ref={navRef}
            className="hidden items-center gap-0.5 lg:flex"
            aria-label="Main navigation"
          >
            {/* Services */}
            <div className="relative">
              <button
                type="button"
                className={`${navLinkClass} flex items-center gap-1 ${
                  openMenu === "services" || servicesActive ? navTriggerActive : ""
                }`}
                aria-expanded={openMenu === "services"}
                aria-haspopup="true"
                onClick={() => toggleMenu("services")}
              >
                Services
                <ChevronDown
                  className={`size-3.5 transition-transform ${openMenu === "services" ? "rotate-180" : ""}`}
                  strokeWidth={1.75}
                  aria-hidden
                />
              </button>
              <AnimatePresence>
                {openMenu === "services" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full z-50 mt-3 w-[min(90vw,480px)] overflow-hidden rounded-2xl glass-strong glow-accent shadow-2xl"
                  >
                    <div className="border-b border-white/10 px-5 py-4">
                      <p className="font-display text-sm font-semibold text-white">What we do</p>
                      <p className="mt-1 text-sm text-zinc-500">
                        Strategy to launch—all under one roof.
                      </p>
                    </div>
                    <ul className="grid max-h-[min(60vh,360px)] gap-0 overflow-y-auto p-2 sm:grid-cols-2">
                      <li className="sm:col-span-2">
                        <Link
                          href="/services"
                          className="block rounded-xl px-4 py-3 transition-colors hover:bg-white/5"
                          onClick={() => setOpenMenu(null)}
                        >
                          <span className="font-medium text-accent">All services</span>
                          <span className="mt-0.5 block text-sm text-zinc-500">
                            Browse the full lineup
                          </span>
                        </Link>
                      </li>
                      {SERVICES.map((service) => (
                        <li key={service.slug}>
                          <Link
                            href={service.href}
                            className="block rounded-xl px-4 py-3 transition-colors hover:bg-white/5"
                            onClick={() => setOpenMenu(null)}
                          >
                            <span className="font-medium text-white">{service.label}</span>
                            <span className="mt-0.5 line-clamp-2 text-sm text-zinc-500">
                              {service.description}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {PRIMARY_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`${navLinkClass} ${pathMatches(pathname, link.href) ? navTriggerActive : ""}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Company — About + Collab live here so they stop competing as peers */}
            <div className="relative">
              <button
                type="button"
                className={`${navLinkClass} flex items-center gap-1 ${
                  openMenu === "company" || companyActive ? navTriggerActive : ""
                }`}
                aria-expanded={openMenu === "company"}
                aria-haspopup="true"
                onClick={() => toggleMenu("company")}
              >
                Company
                <ChevronDown
                  className={`size-3.5 transition-transform ${openMenu === "company" ? "rotate-180" : ""}`}
                  strokeWidth={1.75}
                  aria-hidden
                />
              </button>
              <AnimatePresence>
                {openMenu === "company" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full z-50 mt-3 w-[min(90vw,320px)] overflow-hidden rounded-2xl glass-strong glow-accent shadow-2xl"
                  >
                    <div className="border-b border-white/10 px-5 py-4">
                      <p className="font-display text-sm font-semibold text-white">Company</p>
                      <p className="mt-1 text-sm text-zinc-500">Who we are and how we partner.</p>
                    </div>
                    <ul className="p-2">
                      {COMPANY_LINKS.map((link) => {
                        const Icon = link.icon;
                        const active = pathMatches(pathname, link.href);
                        return (
                          <li key={link.href}>
                            <Link
                              href={link.href}
                              className={`flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-white/5 ${
                                active ? "bg-accent/10" : ""
                              }`}
                              onClick={() => setOpenMenu(null)}
                            >
                              <span
                                className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border ${
                                  active
                                    ? "border-accent/40 bg-accent/15 text-accent"
                                    : "border-white/10 bg-white/[0.04] text-zinc-300"
                                }`}
                              >
                                <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
                              </span>
                              <span>
                                <span className="block font-medium text-white">{link.label}</span>
                                <span className="mt-0.5 block text-sm text-zinc-500">{link.hint}</span>
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Insights */}
            <div className="relative">
              <button
                type="button"
                className={`${navLinkClass} flex items-center gap-1 ${
                  openMenu === "insights" || insightsActive ? navTriggerActive : ""
                }`}
                aria-expanded={openMenu === "insights"}
                aria-haspopup="true"
                onClick={() => toggleMenu("insights")}
              >
                Insights
                <ChevronDown
                  className={`size-3.5 transition-transform ${openMenu === "insights" ? "rotate-180" : ""}`}
                  strokeWidth={1.75}
                  aria-hidden
                />
              </button>
              <AnimatePresence>
                {openMenu === "insights" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-full z-50 mt-3 w-[min(90vw,300px)] overflow-hidden rounded-2xl glass-strong glow-accent shadow-2xl"
                  >
                    <div className="border-b border-white/10 px-5 py-4">
                      <p className="font-display text-sm font-semibold text-white">Insights</p>
                      <p className="mt-1 text-sm text-zinc-500">Reading, playbooks, and tools.</p>
                    </div>
                    <ul className="p-2">
                      {INSIGHTS_LINKS.map((link) => {
                        const Icon = link.icon;
                        const active = pathMatches(pathname, link.href);
                        return (
                          <li key={link.href}>
                            <Link
                              href={link.href}
                              className={`flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-white/5 ${
                                active ? "bg-accent/10" : ""
                              }`}
                              onClick={() => setOpenMenu(null)}
                            >
                              <span
                                className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border ${
                                  active
                                    ? "border-accent/40 bg-accent/15 text-accent"
                                    : "border-white/10 bg-white/[0.04] text-zinc-300"
                                }`}
                              >
                                <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
                              </span>
                              <span>
                                <span className="block font-medium text-white">{link.label}</span>
                                <span className="mt-0.5 block text-sm text-zinc-500">{link.hint}</span>
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              href="/blueprint"
              className={`${navLinkClass} ${pathMatches(pathname, "/blueprint") ? navTriggerActive : ""}`}
            >
              Blueprint
            </Link>
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <Button href={talkHref} size="sm" utmPlacement="navbar-get-started">
              Let&apos;s talk
            </Button>
          </div>

          <button
            ref={menuButtonRef}
            type="button"
            className="flex size-9 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:bg-white/5 lg:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="size-5" strokeWidth={1.75} /> : <Menu className="size-5" strokeWidth={1.75} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-md lg:hidden"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <motion.nav
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed inset-y-0 right-0 z-[120] flex w-[min(100%,26.5rem)] flex-col border-l border-white/10 bg-[#0a0a0a]/95 shadow-[-24px_0_80px_rgba(0,0,0,0.45)] backdrop-blur-2xl lg:hidden"
              aria-label="Mobile navigation"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 pb-4 pt-[max(1.25rem,env(safe-area-inset-top))]">
                <Link
                  href="/"
                  className="min-w-0"
                  aria-label="doimpo home"
                  onClick={() => setMenuOpen(false)}
                >
                  <BrandLogo variant="lockup" markClassName="h-7 w-auto" />
                  <p className="mt-2 max-w-[16rem] font-ui text-xs leading-relaxed text-zinc-400">
                    Offer, company, and insights—clear lanes, one partner.
                  </p>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setMobileServicesOpen(false);
                  }}
                  className="flex size-11 shrink-0 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:bg-white/5"
                  aria-label="Close menu"
                >
                  <X className="size-5" strokeWidth={1.75} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5">
                <p className="mb-2 px-2 font-ui text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Offer
                </p>
                <div className="overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03]">
                  <button
                    type="button"
                    className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-white/[0.04] ${
                      servicesActive ? "bg-accent/10" : ""
                    }`}
                    aria-expanded={mobileServicesOpen}
                    onClick={() => setMobileServicesOpen((open) => !open)}
                  >
                    <span className="flex size-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-accent">
                      <Layers className="size-4" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-display text-[15px] font-semibold text-white">
                        Services
                      </span>
                      <span className="block text-xs text-zinc-500">What we build with you</span>
                    </span>
                    <ChevronDown
                      className={`size-4 text-zinc-400 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileServicesOpen && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden border-t border-white/8"
                      >
                        <li>
                          <Link
                            href="/services"
                            className={`block px-4 py-2.5 pl-[3.5rem] text-sm ${
                              pathname === "/services" ? "text-accent" : "text-zinc-300"
                            }`}
                            onClick={() => setMenuOpen(false)}
                          >
                            All services
                          </Link>
                        </li>
                        {SERVICES.map((service) => (
                          <li key={service.slug}>
                            <Link
                              href={service.href}
                              className={`block px-4 py-2.5 pl-[3.5rem] text-sm ${
                                pathMatches(pathname, service.href)
                                  ? "text-accent"
                                  : "text-zinc-400 hover:text-white"
                              }`}
                              onClick={() => setMenuOpen(false)}
                            >
                              {service.label}
                            </Link>
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                  {OFFER_LINKS.map((link) => {
                    const Icon = link.icon;
                    const active = pathMatches(pathname, link.href);
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`flex items-center gap-3 border-t border-white/8 px-3 py-3 transition-colors hover:bg-white/[0.04] ${
                          active ? "bg-accent/10" : ""
                        }`}
                        onClick={() => setMenuOpen(false)}
                      >
                        <span
                          className={`flex size-9 items-center justify-center rounded-xl border ${
                            active
                              ? "border-accent/40 bg-accent/15 text-accent"
                              : "border-white/10 bg-white/[0.04] text-zinc-300"
                          }`}
                        >
                          <Icon className="size-4" strokeWidth={1.75} aria-hidden />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-display text-[15px] font-semibold text-white">
                            {link.label}
                          </span>
                          <span className="block text-xs text-zinc-500">{link.hint}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>

                <p className="mb-2 mt-6 px-2 font-ui text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Company
                </p>
                <div className="overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03]">
                  {COMPANY_LINKS.map((link, index) => {
                    const Icon = link.icon;
                    const active = pathMatches(pathname, link.href);
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`flex items-center gap-3 px-3 py-3 transition-colors hover:bg-white/[0.04] ${
                          index > 0 ? "border-t border-white/8" : ""
                        } ${active ? "bg-accent/10" : ""}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        <span
                          className={`flex size-9 items-center justify-center rounded-xl border ${
                            active
                              ? "border-accent/40 bg-accent/15 text-accent"
                              : "border-white/10 bg-white/[0.04] text-zinc-300"
                          }`}
                        >
                          <Icon className="size-4" strokeWidth={1.75} aria-hidden />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-display text-[15px] font-semibold text-white">
                            {link.label}
                          </span>
                          <span className="block text-xs text-zinc-500">{link.hint}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>

                <p className="mb-2 mt-6 px-2 font-ui text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  Insights
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[...INSIGHTS_LINKS, {
                    href: "/blueprint",
                    label: "Blueprint",
                    hint: "Plan your next move",
                    icon: Compass,
                  } satisfies NavItem].map((link) => {
                    const Icon = link.icon;
                    const active = pathMatches(pathname, link.href);
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        className={`flex min-h-[5.5rem] flex-col justify-between rounded-2xl border px-3 py-3 transition-colors ${
                          active
                            ? "border-accent/40 bg-accent/10"
                            : "border-white/8 bg-white/[0.03] hover:bg-white/[0.06]"
                        }`}
                        onClick={() => setMenuOpen(false)}
                      >
                        <Icon
                          className={`size-4 ${active ? "text-accent" : "text-zinc-400"}`}
                          strokeWidth={1.75}
                          aria-hidden
                        />
                        <span>
                          <span className="block font-display text-sm font-semibold text-white">
                            {link.label}
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">
                            {link.hint}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-white/10 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4">
                <Button href={talkHref} className="w-full" utmPlacement="navbar-get-started-mobile">
                  Let&apos;s talk
                </Button>
                <div className="mt-3 flex items-center justify-center gap-2">
                  {SOCIAL_LINKS.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.href}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        className="flex size-11 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-accent/40 hover:text-accent"
                      >
                        <Icon className="size-4" strokeWidth={1.75} aria-hidden />
                      </a>
                    );
                  })}
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
